import LegalPage from "@/components/LegalPage";

const BODY = `## 1. Who we are

HaulCheck ("we") provides compliance management software for haulage operators in the UK and Ireland, covering MOT/CVRT status, driver licence and Driver CPC monitoring, tachograph analysis, operator licence documentation, defect reporting, audit packs, and AI-based risk scoring, via our platform and companion driver app.

Data Controller: HaulCheck

Registered address: "Hillcrest", 25 High Street, Monaghan, Ireland, H18KD29

Contact: info@haulcheck.co.uk

ICO registration number: [add if registered]

## 2. What data we collect

**From operators (account holders):**

- Company name, address, operator licence number
- Account contact name, email, phone number
- Billing information

**From/about drivers (via operator input or the driver app):**

- Name, driver licence number and category, licence expiry
- Driver CPC status and expiry dates
- Tachograph activity data (driving/rest periods, infringements)
- Vehicle assignment and defect report submissions

**From vehicles:**

- Registration, MOT/CVRT status and history
- Defect reports and audit pack documentation

**Technical data:**

- Login and usage data, IP address, device/browser type, for security and service improvement

## 3. Why we process this data (lawful basis)

| Purpose | Lawful basis |
|---|---|
| Providing the compliance monitoring service you've signed up for | Contract (performance of our agreement with you) |
| Supporting your regulatory obligations to DVSA/RSA | Legitimate interests (yours and ours, in enabling compliance) |
| AI risk scoring and audit pack generation | Legitimate interests |
| Billing and account administration | Contract / legal obligation |
| Marketing communications (optional) | Consent |
| Responding to legal or regulatory requests | Legal obligation |

Where we rely on legitimate interests, we've assessed that this doesn't override the rights and freedoms of drivers or operators — the processing exists to help you meet mandatory transport compliance obligations.

## 4. Who this data belongs to

Fleet and driver data entered into HaulCheck belongs to the operator (you). HaulCheck acts as a **data processor** on your behalf for driver and vehicle compliance records, and as a **data controller** for account, billing, and platform usage data. A Data Processing Agreement (DPA) is available on request and forms part of our terms for business customers.

## 5. How we use your data

We use the data described above solely to:

- Deliver compliance monitoring, alerts, and audit packs
- Generate AI risk scores based on your fleet's compliance history
- Provide the driver-facing app so drivers can view their own compliance status
- Maintain and improve the security and reliability of the platform
- Communicate service updates, and marketing only where you've opted in

We do not sell fleet or driver data, and we do not use it to train third-party AI models outside the service you've contracted.

## 6. Data retention

- **Tachograph and driver compliance records**: retained in line with statutory retention requirements (e.g. tachograph record-keeping rules under UK/EU drivers' hours regulations) or for the duration of your active subscription, whichever is longer, unless you request earlier deletion where not legally restricted.
- **Account and billing data**: retained for the duration of your account plus 6 years to meet accounting and tax obligations.
- **Driver app data**: retained while the driver is active with your fleet; removed on request or on offboarding, subject to statutory retention overrides.

On account closure, we'll retain data only as long as legally required, then securely delete or anonymise it.

## 7. Where your data is stored

Data is hosted on cloud infrastructure provided by Emergent (Google Cloud, US region). Data is encrypted in transit (TLS) and at rest. We do not transfer personal data outside the UK/EEA except where a sub-processor requires it, in which case appropriate safeguards (e.g. Standard Contractual Clauses) apply.

## 8. Who we share data with

- **Sub-processors**: hosting (Emergent (emergent.sh)) and other infrastructure providers necessary to run the service, bound by data processing agreements.
- **Regulators**: only where legally compelled (e.g. a DVSA/RSA investigation), and only to the extent required.
- We do not share data with third parties for their own marketing purposes.

A full list of sub-processors is available on request.

## 9. Your rights

Under UK GDPR, you (and drivers whose data is processed) have the right to:

- Access the personal data we hold
- Correct inaccurate data
- Request erasure, subject to statutory retention requirements that may override this for compliance records
- Restrict or object to certain processing
- Data portability
- Withdraw consent for marketing at any time

To exercise these rights, contact info@haulcheck.co.uk. Operators are responsible for handling data subject requests from their own drivers where HaulCheck acts as processor; we'll support you in fulfilling these.

You also have the right to complain to the UK Information Commissioner's Office (ico.org.uk) or, for Irish operators, the Data Protection Commission (dataprotection.ie).

## 10. Security

We use encryption in transit and at rest, role-based access controls limiting who can view driver-level data, and regular security review of our infrastructure. No system is 100% secure, but we take reasonable technical and organisational measures to protect your data.

## 11. Changes to this policy

We'll update this policy as needed and notify account holders of material changes. Continued use of HaulCheck after changes take effect constitutes acceptance.

## 12. Contact

Questions about this policy or your data: info@haulcheck.co.uk
`;

export default function PrivacyPolicy() {
  return <LegalPage testid="privacy-policy-page" title="Privacy Policy" lastUpdated="17 August 2026" body={BODY} />;
}
